/**
 * The RPS Hub's transactional email catalogue (DATA-3029).
 *
 * ONE SendGrid dynamic template per email type. Before DATA-3029 every email
 * went through a single generic template that received the whole message as one
 * `body_content` string — SendGrid rendered it as a single block, losing line
 * breaks and formatting. The prose now lives in the templates (owned by TRP
 * staff in SendGrid: Email API > Dynamic Templates) and the backend sends only
 * variables.
 *
 * This registry is the single source of truth for both:
 *  - the admin "Transactional Email Types" table (template name / id / trigger /
 *    test button), and
 *  - the Email Logs table's "Event Trigger" column
 *    ({@link EMAIL_LOG_EVENT_TRIGGER_LABELS} is derived from it).
 *
 * It lives in `interfaces` (shared FE + API) because template ids are not
 * secrets — the same reasoning that already kept them in code rather than in
 * environment config.
 */

/**
 * Variables every template expects, whatever the email type. Documented in
 * Notion ("Default Settings") and built by `defaultTemplateData` on the API side.
 *
 * `base_url` always ends with a slash (templates append the page name).
 */
export const DEFAULT_TEMPLATE_VARIABLES = [
  'community_name',
  'recipient_first_name',
  'tool_name',
  'team_name',
  'base_url',
] as const;

/** One entry of the catalogue. */
export interface TransactionalEmailTypeDefinition {
  /**
   * Logical email type. PERSISTED on every `email-log` row, so these slugs must
   * never be renamed — the Event Trigger labels are what changed in DATA-3029
   * ("Access Request" → "Community Access Request").
   */
  emailType: string;
  /** Label shown in the "Event Trigger" column of both admin tables. */
  eventTriggerLabel: string;
  /** Name of the template in SendGrid (Email API > Dynamic Templates). */
  templateName: string;
  /**
   * SendGrid dynamic template id. Absent for the triggers that have no template
   * yet: the AC asks for those rows to be listed all the same, with their test
   * button disabled (falling back to the old generic template would reintroduce
   * exactly the `body_content` problem this ticket removes).
   */
  templateId?: string;
  /**
   * Variables this template needs ON TOP of
   * {@link DEFAULT_TEMPLATE_VARIABLES}. Empty when the template only uses the
   * defaults.
   */
  additionalVariables: readonly string[];
}

/** `action_button_url` — the deep link behind a template's action button. */
const ACTION_BUTTON_URL = 'action_button_url';

/**
 * Who performed the action, for the emails sent to somebody else (the Owners of
 * a community, or the author of a request another user edited).
 *
 * `submitting_user_name` is sent alongside `submitting_user_full_name` with the
 * same value: Notion lists `submitting_user_full_name` in the variables table
 * but the template bodies interpolate `{{submitting_user_name}}`. Sending both
 * keeps whichever the templates actually use rendering.
 */
const SUBMITTING_USER_VARIABLES = [
  'submitting_user_full_name',
  'submitting_user_name',
  'submitting_user_first_name',
  'submitting_user_email',
] as const;

export const TRANSACTIONAL_EMAIL_TYPES: readonly TransactionalEmailTypeDefinition[] =
  [
    {
      emailType: 'access-request-more-info-needed',
      eventTriggerLabel: 'Community Access Request: More Info Needed',
      templateName: 'Community Access Request: More Information Needed',
      templateId: 'd-fffa35dd968f4fb59a5366d7cffbce3b',
      additionalVariables: [],
    },
    {
      emailType: 'access-request-approved',
      eventTriggerLabel: 'Community Access Request: Approved',
      templateName: 'Community Access Request: Approved',
      templateId: 'd-7648179324f847ee81b84ea6b90da886',
      additionalVariables: [ACTION_BUTTON_URL],
    },
    {
      emailType: 'access-request-rejected',
      eventTriggerLabel: 'Community Access Request: Rejected',
      templateName: 'Community Access Request: Rejected',
      templateId: 'd-3697f9cc1eaf4439a7a96495b50c0068',
      additionalVariables: [],
    },
    {
      emailType: 'data-update-submitted',
      eventTriggerLabel: 'Data Update Request: Submitted (to submitting user)',
      templateName: 'Data Update Request: Submitted (to submitting user)',
      templateId: 'd-4ae5c7c51fa64f198d66b37fc8ce8401',
      additionalVariables: [ACTION_BUTTON_URL],
    },
    {
      emailType: 'data-update-submitted-owner',
      eventTriggerLabel: 'Data Update Request: Submitted (to all Owners)',
      templateName: 'Data Update Request: Submitted (to all Owners)',
      templateId: 'd-804afd99ebd04055a47be91f809be113',
      additionalVariables: [...SUBMITTING_USER_VARIABLES, ACTION_BUTTON_URL],
    },
    {
      emailType: 'data-update-modified',
      eventTriggerLabel: 'Data Update Request: Modified (by other user)',
      templateName: 'Data Update Request: Modified (by other user)',
      templateId: 'd-1919738ee73c497aa57fff976b1ca070',
      additionalVariables: [...SUBMITTING_USER_VARIABLES, ACTION_BUTTON_URL],
    },
    {
      emailType: 'data-update-approved',
      eventTriggerLabel: 'Data Update Request: Approved',
      templateName: 'Data Update Request: Approved',
      templateId: 'd-4ca0c5fb1df3461bbd15c82093afc1cb',
      additionalVariables: [ACTION_BUTTON_URL],
    },
    {
      // Deliberately NOT wired to a template: this email is composed and sent
      // from the RPS Approvals app (Retool), which hands it the custom content
      // the reviewer typed (DATA-3071). The RPS Hub never sends it, so it will
      // never appear in the Email Logs — listed here so the gap reads as
      // intentional.
      emailType: 'data-update-more-info-needed',
      eventTriggerLabel: 'Data Update Request: More Info Needed',
      templateName: 'Data Update Request: More Info Needed',
      additionalVariables: [],
    },
    {
      // Deliberately NO email at all when a data update request is rejected.
      // Kept on the list so nobody re-adds it thinking it was forgotten.
      emailType: 'data-update-rejected',
      eventTriggerLabel: 'Data Update Request: Rejected',
      templateName: 'Data Update Request: Rejected',
      additionalVariables: [],
    },
    {
      // The agreed logic is one template with two bodies, switched on
      // `is_rescinding_user`: the user who rescinded reads "your request has
      // been rescinded", everyone else reads "rescinded by <manager>".
      emailType: 'data-update-rescinded',
      eventTriggerLabel: 'Data Update Request: Rescinded',
      templateName: 'Data Update Request: Rescinded',
      templateId: 'd-07897261d91247ef9b65bb71155dd3d9',
      additionalVariables: [...SUBMITTING_USER_VARIABLES, 'is_rescinding_user'],
    },
    {
      emailType: 'screen-reader-friendly-pdf-request',
      eventTriggerLabel: 'Screen Reader-Friendly PDF Request',
      templateName: 'Screen Reader-Friendly PDF Request: Submitted',
      templateId: 'd-11da5f216d4241a9bfa196ac375aa33f',
      additionalVariables: [], //TODO: add the PROJECT_NAME variable
    },
    {
      emailType: 'drop-off-location-bulk-edit-request',
      eventTriggerLabel: 'Drop-Off Location Bulk Edit Request',
      templateName: 'Drop-Off Locations: Bulk Edit Request Made',
      templateId: 'd-87ed8ae78b204046add9ee013b480523',
      additionalVariables: [],
    },
  ];

/** The catalogue entry for an email type, `undefined` for an unknown slug. */
export const transactionalEmailType = (
  emailType: string
): TransactionalEmailTypeDefinition | undefined =>
  TRANSACTIONAL_EMAIL_TYPES.find((entry) => entry.emailType === emailType);

/**
 * Display label per email type for the "Event Trigger" column of the Email Logs
 * table. Derived from the catalogue so the two admin tables can never disagree;
 * unknown types (older rows, test sends) fall back to a humanised slug on the
 * front-end.
 */
export const EMAIL_LOG_EVENT_TRIGGER_LABELS: Record<string, string> =
  Object.fromEntries(
    TRANSACTIONAL_EMAIL_TYPES.map((entry) => [
      entry.emailType,
      entry.eventTriggerLabel,
    ])
  );

/**
 * SendGrid's editor for one dynamic template — the link behind the Template ID
 * column (DATA-3029).
 *
 * The id in the URL is the very id SendGrid's own UI uses, so this is not the
 * mistake the Message ID column made (there the id itself was wrong: the short
 * `X-Message-Id` never resolves to an Activity record). Worst case, if SendGrid
 * changes its console routing, the link lands on the Dynamic Templates list —
 * still where an admin is going, not a dead end. It requires a SendGrid login,
 * which every TRP admin who edits templates already has.
 */
export const sendGridTemplateEditUrl = (templateId: string): string =>
  `https://mc.sendgrid.com/dynamic-templates/${templateId}`;

/** Names the tool in an email and signs it — `tool_name` / `team_name`. */
export interface ToolBranding {
  toolName: string;
  teamName: string;
}

const SOLUTIONS_HUB: ToolBranding = {
  toolName: 'Recycling Program Solutions Hub',
  teamName: 'The Solutions Hub Team',
};

/**
 * States with their own Education & Outreach Portal branding — the same two the
 * portal landing pages cover (`CAA_LANDING_CONFIGS`). Deliberately NOT
 * `EDUCATIONAL_MATERIALS_STATES`, which also lists Arkansas and Texas: those are
 * eligible for Educational Materials but have no portal of their own.
 */
const PORTAL_BRANDING: Record<string, ToolBranding> = {
  colorado: {
    toolName: 'Colorado Education & Outreach Portal',
    teamName: 'Colorado Education & Outreach Portal Team',
  },
  oregon: {
    toolName: 'Oregon Education & Outreach Portal',
    teamName: 'Oregon Education & Outreach Portal Team',
  },
};

/**
 * Which tool to name for a community, from its state. Falls back to the RPS Hub
 * for every non-portal state (and when the state is missing).
 */
export const toolBrandingForState = (stateName?: string): ToolBranding =>
  PORTAL_BRANDING[stateName?.trim().toLowerCase() ?? ''] ?? SOLUTIONS_HUB;
